const recruitingAiV2 = {
  // The old recruiting agent's product wording (no version in the UI).
  productName: 'AI Search',
  // Shown when a chat turn fails for lack of AI points (V2's own copy of the old page's text).
  insufficientCredits: {
    title: 'Insufficient AI Points',
    description: 'Starting a new Agent chat requires 20 AI points. Please get more points first.',
  },
  // The old recruiting agent's bottom input (composer.placeholder / hint) and the toolbar above it
  // (composer.menus / executeSearch / searchVersionConfirm), with the owner's V2 changes: no Change company
  // or Boolean Search, Keywords is Keyword & Skill, Scoring is ICP profile. Clicking a menu item puts it in
  // the input.
  composer: {
    placeholder: 'Keep refining the search strategy or enter a new instruction...',
    send: 'Send',
    hint: 'Press Enter to send, Shift + Enter for a new line',
    executeSearch: 'Start sourcing',
    executeDisabledReason: 'Confirm the profile to get a search strategy, then start sourcing',
    icpProfile: 'ICP profile',
    searchVersionConfirm: {
      title: 'Choose your LinkedIn account type',
      options: {
        business: 'LinkedIn (standard account)',
        sales: 'LinkedIn Sales (Sales)',
        recruiter: 'LinkedIn Recruiter (Recruiter)',
      },
    },
    searchVersionSelected: '{version} automation search mode selected',
    salesNavigatorStarted: 'Sales Navigator search started. The extension will fill in the filters on the LinkedIn page.',
    salesNavigatorConnectionLost: 'The search page was closed or the connection was lost. Please click Start sourcing again.',
    // Standard LinkedIn (Business): Start sourcing opens a people-search link in a new tab; no automated sourcing.
    searchVersionSelectedBusiness: '{version} selected. Start sourcing opens a LinkedIn people search in a new tab',
    businessSearchOpened: 'Opened LinkedIn people search in a new tab',
    businessSearchPopupBlocked: 'Your browser blocked the new tab. Use the button to open the LinkedIn people search',
    businessSearchOpenLink: 'Open search',
    businessSearchLocationsOmitted: "Locations weren't added to the link; choose them on the LinkedIn page",
    businessSearchUnavailable: 'The search strategy has no job titles or keywords yet, so the LinkedIn people search cannot be opened',
    menus: {
      companies: {
        label: 'Target companies',
        items: [
          'Direct competitors only',
          'Add mid-market companies',
          'Add local companies',
          'Add upstream companies',
          'Add downstream channel companies',
          'Add similar sales models',
          'Add similar products',
          'Add same tech stack companies',
          'Add same-industry companies',
          'Add same customer profile companies',
          'Add China-linked global companies',
          'Exclude companies',
        ],
      },
      titles: {
        label: 'Job titles',
        items: [
          'Narrow core titles',
          'Expand titles',
          'Add seniority variants',
          'Generate by target company',
          'Remove irrelevant titles',
        ],
      },
      keywords: {
        label: 'Keyword & Skill',
        items: ['Keep only core keywords', 'Expand related keywords'],
      },
    },
  },
  // A new conversation's welcome page, from the old recruiting agent's (jobCreate.*); the subtitle and
  // the first card are the owner's V2 wording.
  welcome: {
    title: '{name}, describe your hiring need',
    placeholder:
      'E.g.: "Looking for a Senior AI Engineer, 3+ years LLM / RAG / Agent experience, Shanghai or remote, fluent English, open-source work preferred"',
    send: 'Send',
    capabilitiesLabel: 'EasyHire AI capabilities',
    talentRecommendTitle: 'Instant talent recommendations',
    talentRecommendDesc: 'AI talent recommendations in seconds from public data across GitHub, X, LinkedIn, and more',
    linkedinSearchTitle: 'LinkedIn automation search',
    linkedinSearchDesc:
      'The extension runs the search plan on Recruiter pages and scores resumes after they sync back',
    newBadge: 'NEW',
    example1Label: 'LLM Researcher',
    example1Prompt:
      'Hiring a senior LLM researcher based in Beijing for pre-training and alignment (RLHF / DPO), with experience training models with hundreds of billions of parameters. Top-tier conference publications preferred. Target companies: ByteDance, Alibaba Tongyi, and Moonshot AI.',
    example2Label: 'International Energy Storage Sales',
    example2Prompt:
      'Hiring a European energy storage sales director based in Munich, Germany, to develop commercial, industrial, and grid-scale energy storage projects across Europe. English or German as a working language, with 8+ years of new energy sales experience.',
    example3Label: 'International HR',
    example3Prompt:
      'Hiring a mid-level HRBP based in Dubai, UAE, to support recruitment, performance management, and labor compliance for a team of around 300 across the Middle East. English is the working language; experience supporting Chinese companies expanding overseas is required.',
  },
  // The voice button (welcome and bottom inputs), copied from the old recruiting agent's (composer.*Voice*).
  voice: {
    start: 'Voice input',
    stop: 'Stop voice input',
    unsupported: 'Voice input is not supported in this browser',
    permissionDenied: 'Microphone permission is required for voice input',
    failed: 'Voice input failed. Please try again.',
    status: {
      idle: 'Click the microphone to start voice input',
      starting: 'Opening the microphone. Start speaking...',
      listening: 'Listening to you...',
      processing: 'Processing your voice input...',
      success: 'Voice input has been inserted',
      empty: 'Could not hear clearly. Try again.',
      error: 'Voice input failed. Please try again.',
    },
  },
  chat: {
    searching: 'Searching...',
    deepSearch: 'Start deep search',
    searchCompleted: 'Search completed',
    openSearchPack: 'Search completed. View Pack {pack} candidates',
    searchPlatformsIllustration: 'Public profile platforms · illustration',
    searchTargetCompanies: 'Target companies',
    searchMoreCompanies: 'More target companies',
    expandMessage: 'Show more',
    collapseMessage: 'Show less',
    degradedNotice: 'The reply may be incomplete — please double check before relying on it.',
    errorRetry: 'Something went wrong. Please try sending your message again.',
    // Admin mode, another user's conversation: view only.
    readOnly: 'This conversation belongs to another user. You can view it but cannot send messages or change the strategy.',
    // A page refresh interrupted the last reply (fallback of 2026-10-16).
    interruptedNotice: 'The last reply was interrupted. Please send it again.',
    confirmIcpFailed: "Couldn't confirm the profile. Please try again.",
    // Must match the history text the backend saves for a role overview edit (step 11 B-2).
    roleSummaryEdited: 'I updated the role overview: {text}',
    roleSummaryEditFailed: "Couldn't update the role overview. Please try again.",
    // Step 20 P19: the small tag above a message saying where it came from (never part of its text).
    source: {
      linkedinFeedback: 'LinkedIn feedback',
      instantRecommend: 'Instant Recommend',
    },
    // Q10: a turn or a confirm that has waited 20 s.
    longWait: 'Still working…',
    // Step 20 P17: what the Agent is doing before its reply text starts (`run.progress` steps).
    progress: {
      received: 'Understanding your request…',
      profile: 'Preparing the role profile…',
      strategy: 'Generating the search strategy…',
      companies: 'Searching for and updating target companies…',
      reply: 'Preparing the reply…',
    },
    initialProfile: {
      intro: 'I’ll review your requirements and prepare the candidate profile.',
      steps: { received: 'Reviewing your request and role', profile: 'Building the candidate profile', strategy: 'Preparing search criteria', reply: 'Preparing the reply' },
      done: { received: 'Role requirements received', profile: 'Candidate profile created', strategy: 'Search criteria prepared', reply: 'Reply prepared' },
      details: { received: 'Checking the role, location and hiring requirements', profile: 'Organizing requirements, preferences and mismatch signals', strategy: 'Organizing job titles, keywords and target companies', companies: 'Searching for and updating target companies', reply: 'Preparing the summary and any questions to confirm' },
      strategyCounts: 'Job titles: {titles} · Keywords: {keywords} · Target companies: {companies}',
    },
  },
  // Step 20: a new-format reply's changes card, clarification and action buttons.
  turn: {
    changesTitle: 'This turn updated:',
    changeTargets: {
      icp: 'Profile',
      strategy: 'Search strategy',
    },
    fieldSeparator: ', ',
    actionLabels: {
      confirm_icp: 'Confirm profile',
      open_icp: 'Open profile',
      open_strategy: 'Open strategy',
      instant_recommend: 'Instant recommendations',
      linkedin_search: 'Start sourcing',
    },
  },
  // Step 20 §2 / §3: the confirm card (three skippable fields) and what happens after the confirm.
  confirm: {
    title: 'Confirm the profile',
    optionalHint: 'All three are optional; leave them empty to confirm as is',
    skippable: 'Optional',
    fields: {
      hiring_company: 'Hiring company',
      industry: 'Industry',
      business_type: 'Business type',
    },
    preparing: 'Building the search strategy…',
    confirmDirectly: 'Confirm as is',
    retry: 'Retry',
    strategyReady: 'Search strategy ready',
    strategyFailed: "Couldn't build the search strategy: {reason}",
    preparationErrors: {
      timeout: 'it timed out',
      failed: 'something went wrong',
      incomplete: 'the strategy is still incomplete',
      unknown: 'unknown reason',
    },
    recommend: {
      starting: 'Starting instant recommendations…',
      started: 'Instant recommendations started',
      conflict: 'The search strategy changed and the latest version is loaded. Review it, then click Instant recommendations',
      not_started: "Instant recommendations didn't start. Click Instant recommendations on the strategy card to try again",
    },
    errors: {
      invalid_optional_context: 'Some of these details are not valid. Please fix them and confirm again',
      structured_icp_required: "This older profile can't take these details. You can confirm it as is",
      icp_version_not_found: 'This profile version no longer exists. The current profile is loaded; keep chatting to get a new one to confirm',
      icp_version_conflict: 'The profile changed; confirm the latest one',
      retryable: "The confirm didn't finish and may already be saved. You can retry (nothing changes)",
      failed: "Couldn't confirm the profile. Please try again",
    },
  },
  suggestion: {
    // Chip labels for suggestion actions whose own `text` is empty (e.g. the ICP-confirmation
    // template's `revise_icp`, which fills the composer with nothing).
    fallbackLabels: {
      revise_icp: 'Needs changes',
    },
  },
  icp: {
    title: 'Ideal Candidate Profile',
    confirmed: 'Confirmed',
    waiting: 'Awaiting confirmation',
    viewDetails: 'View details',
    legendConfirmed: 'Confirmed',
    updating: 'Updating…',
    updated: 'Updated',
    version: 'Version {version}',
    updatedAt: 'Updated {time}',
    justNow: 'just now',
    notMentioned: 'Not mentioned',
    origin: {
      confirmed: 'Confirmed',
      inferred: 'Inferred',
    },
    empty: 'No profile yet.',
    noMustHave: 'None yet',
    noMismatch: 'None yet',
    fields: {
      hiringCompany: 'Hiring Company',
      roleOverview: 'Role Overview',
      mustHave: 'Must-Have Requirements',
      mismatch: 'Mismatch Signals',
      title: 'Title',
      preferred: 'Preferred Criteria',
      typicalSources: 'Typical Sources',
      searchSuggestions: 'Search Suggestions',
      industry: 'Industry',
      businessType: 'Business Type',
    },
  },
  filter: {
    title: 'Search Strategy',
    viewDetails: 'View details',
    started: 'Started',
    recommendButton: 'Instant Recommend',
    recommendRunning: 'Searching…',
    linkedinButton: 'Start sourcing',
    readonlyNote: 'Update the strategy in View details or continue in chat, then click Start sourcing for the next round',
    fields: {
      companies: 'Target companies',
      excludedCompanies: 'Excluded companies',
      jobTitles: 'Job titles',
      keywords: 'Keywords',
    },
  },
  strategy: {
    sections: {
      core: 'Search core',
      companies: 'Companies',
      background: 'Candidate background',
      education: 'Education & more',
      advanced: 'Advanced filters',
    },
    fields: {
      titles: 'Job titles',
      coreKeywords: 'Keyword & Skill',
      locations: 'Location',
      companies: 'Target companies',
      excludedCompanies: 'Excluded companies',
      industries: 'Industries',
      jobFunctions: 'Job functions',
      seniority: 'Seniority',
      languages: 'Languages',
      yearsOfExp: 'Years of experience',
      currentRoleYears: 'Years in current role',
      jobHop: 'Job hopping',
      schools: 'Schools',
      majors: 'Majors',
      degrees: 'Degrees',
      isChinese: 'Chinese',
      chinaExperience: 'Chinese-company experience',
      // Fields with no drawer control: named only where a change lists them.
      narrowKeywords: 'Preferred keywords',
      excludedKeywords: 'Excluded keywords',
      excludedTitles: 'Excluded job titles',
      workArrangement: 'Work arrangement',
      languagePriority: 'Language priority',
      ageRange: 'Age',
      workLocationScope: 'Location match scope',
      companyRecommendations: 'Company recommendation reasons',
      companyGroupScope: 'Company group scope',
    },
    locationScopeLabel: 'Match',
    locationScope: {
      current: 'Current work location',
      past: 'Past work locations',
    },
    companyScopeLabel: 'Target company match scope',
    companyScope: {
      any: 'Current or past',
      current: 'Current only',
      past: 'Past only',
    },
    jobHop: {
      noLimit: 'Any',
      threeYears: 'At most 2 positions in the last 3 years',
      fiveYears: 'At most 3 positions in the last 5 years',
      lastTwoEachTwoYears: 'Each of the last 2 positions lasted at least 2 years',
    },
    chinaExperience: {
      any: 'Any',
      yes: 'Has worked at a Chinese company',
      no: 'Has not worked at a Chinese company',
      noHint: 'Only matches candidates explicitly marked as no',
    },
    showReasons: 'Show reasons',
    hideReasons: 'Hide reasons',
    noReason: 'No reason available',
    manualAdded: 'Added manually',
    expandMore: '+{count} more',
    collapse: 'Show less',
    unlimited: 'Any',
    changedCount: 'Changes: {count}',
    undo: 'Undo',
    saveStatus: {
      saving: 'Saving…',
      recognizingRegions: 'Recognizing regions…',
      saved: 'Saved',
      regionUnrecognized: 'This region couldn\'t be recognized. Please rewrite it as specific countries',
      failed: 'Save failed',
      rejected: 'Save failed: the strategy did not pass validation',
      invalidDraft: 'Some values are invalid; fix them to save',
      retry: 'Retry',
      conflict: 'The strategy was updated elsewhere. The latest version is loaded; edit again if needed',
      dismiss: 'Dismiss',
      blocked: 'The Search Strategy is not saved yet. Resolve it in the strategy details before starting a search',
      searchVersionConflict: 'The search strategy was updated. The latest version is loaded; review it and search again',
      searchVersionReloadFailed: 'The search strategy was updated, but the latest version could not be loaded. Refresh the page before searching',
    },
    capHint: 'Up to {max}',
    capExceeded: 'Up to {max}; remove the extra items',
    tagInput: {
      reviewHint: 'The pasted text splits into these entries. Add them?',
      addAll: 'Add all',
      cancel: 'Cancel',
      splitByComma: 'Split at commas',
      unmatched: 'Not matched',
      overCap: '{count} not added: up to {max}',
      ambiguous: 'Several places have this name; pick one',
      levels: { city: 'City', region: 'State/Province', country: 'Country', area: 'Area', region_group: 'Region', metro: 'Metro area' },
    },
    options: {
      loading: 'Loading options…',
      failed: 'Could not load options; not editable for now',
      unavailable: 'No options available; not editable for now',
      retry: 'Retry',
      nonStandard: 'Non-standard',
      invalidOption: 'Has non-standard values; remove them to save',
    },
    removeItem: 'Remove {item}',
    addField: 'Add {field}',
    selectField: 'Choose {field}',
    addPlaceholder: '+ Add',
    selectPlaceholder: '+ Choose',
    emptyPlaceholder: '{empty} · {action}',
    placeholders: {
      titles: '+ Add a job title, press Enter',
      coreKeywords: '+ Add a keyword or skill',
      locations: '+ Add a location',
      companies: '+ Add a company',
      excludedCompanies: '+ Add a company to exclude',
    },
    rangeMin: '{field} minimum',
    rangeMax: '{field} maximum',
    rangeTo: 'to',
    rangeUnit: 'years',
    rangeAtLeast: '{min}+ years',
    rangeAtMost: 'Up to {max} years',
    rangeErrors: {
      reversedRange: 'The minimum cannot be greater than the maximum',
      invalidNumber: 'Enter 0 or a whole number',
    },
  },
  drawer: {
    close: 'Close',
  },
  radar: {
    noResultsNotice: 'AI Continuous Sourcing will continue sourcing suitable talent from public data worldwide and notify you when matches are found, or you can start a LinkedIn search to find matching talent.',
    searchTimeoutNotice: 'Still sourcing: the {count} people found so far are kept. You can start a deep search.',
    deepSearchTimeoutNotice: 'The deep search has ended: the {count} people found are kept.',
    nextBatch: 'See more talent',
    nextBatchHint: 'Keep sourcing the next batch for this search',
    nextBatchFailed: 'The next batch could not be sourced right now. Please try again later.',
    candidatePacks: 'Candidate Packs',
    selectPack: 'Select candidate pack',
    reviewTabsLabel: 'Candidate review',
    reviewTabs: { all: 'Full pack', unreviewed: 'Unreviewed', yes: 'Yes', no: 'Rejected' },
    openPanel: 'Show recommendations',
    closePanel: 'Hide recommendations',
    resizePanel: 'Resize recommendations panel',
    status: { idle: 'Not started', running: 'Searching', completed: 'Completed', cancelled: 'Cancelled', failed: 'Search failed' },
    relaxedCount: 'Relaxed {count} time(s)',
    // C1 / C2: people meeting the hard conditions, and how many are shown.
    matchTotal: 'Talent pool: {count}',
    matchTotalCapped: 'Talent pool: 1k+',
    shownCount: '{count} recommended',
    stop: 'Stop',
    tiles: { relatedTerms: 'Related terms', recalled: 'Recalled', found: 'Recommended ›', highMatch: 'High match ›' },
    stages: { plan: 'Plan', recall: 'Recall', assess: 'Evaluate' },
    stagesLabel: 'Search progress',
    counterPending: 'Counting',
    loading: 'Searching the talent pool…',
    failure: {
      timeout: 'The search timed out before it finished.',
      stream_ended: 'The search was interrupted before all results came back.',
      generic: 'The search did not finish.',
      cancelled: 'You stopped this recommendation.',
    },
    retry: 'Retry',
    recommendAgain: 'Recommend again',
    emptyList: 'No recommendations yet',
    allAddedToJob: 'Everyone in this batch was added to the job',
    emptyIntro: 'No recommendations yet',
    emptyIntroHint:
      'Confirm the talent profile and search strategy, then click "Instant Recommend". It searches the talent pool per the strategy — progress and results show up here.',
    recommendButton: 'Instant Recommend',
    viewGraph: 'Graph',
    viewList: 'List',
    filterAll: 'All',
    filterHigh: 'High match',
  },
  candidate: {
    expand: 'Expand candidate information',
    collapse: 'Collapse candidate information',
    previewRole: 'Role',
    previewCompany: 'Most recent company',
    detailFields: {
      name: 'Name',
      full_name: 'Name',
      candidate_name: 'Candidate',
      title: 'Title',
      headline: 'Headline',
      current_title: 'Current Title',
      current_company: 'Current Company',
      company: 'Company',
      organization: 'Organization',
      location: 'Location',
      city: 'City',
      country: 'Country',
      summary: 'Summary',
      bio: 'Bio',
      description: 'Description',
      profile_url: 'Profile URL',
      url: 'URL',
      linkedin_url: 'LinkedIn',
      start_date: 'Start Date',
      end_date: 'End Date',
      school: 'School',
      degree: 'Degree',
      major: 'Major',
      email: 'Email',
      phone: 'Phone',
      source: 'Source',
      candidate_id: 'Candidate ID',
      role: 'Current Role',
      score: 'Match Score',
      match_level: 'Match Level',
      work_experience: 'Total Experience',
      work_experience_no_gap: 'Continuous Experience',
      birth_year: 'Birth Year',
      is_chinese: 'Chinese Candidate',
      working_status: 'Working Status',
      function_id: 'Function ID',
      organization_id: 'Organization ID',
      institution_id: 'Institution ID',
      major_id: 'Major ID',
      employment_type: 'Employment Type',
      location_type: 'Location Type',
      work: 'Work Record',
      project: 'Project Evidence',
      publicSource: 'Public Source',
      open: 'Open',
      present: 'Present',
    },
    unnamed: 'Unnamed Candidate',
    present: 'Present',
    companyEvidence: 'Target company',
    add: 'Add to Job',
    added: 'Added',
    addedToJob: 'Added to Job',
    selectCandidate: 'Select {name}',
    aiMatchLockedTitle: '{count} more potential candidates are waiting for you',
    aiMatchLockedDescription: 'Your AI credits have run out. Add credits to keep unlocking candidate profiles and not miss a good fit.',
    aiMatchUpgrade: 'Keep unlocking',
    aiMatchUnlocked: '{count} people unlocked',
    aiMatchUnlockedPartial: '{count} people unlocked; not enough credits, {locked} are still locked',
    selectAll: 'Select current list',
    selectedCount: '{count} selected',
    bulkAdd: 'Add selected to job',
    bulkBinding: 'Adding…',
    bulkSuccess: 'Added {count} candidates to job',
    bulkPartial: 'Added {success}; {failed} failed and can be retried',
    rejectedBindHint: 'Undo rejection before adding this candidate to the job.',
    noRunHint: 'This result is from an earlier search. Run a new search to add it to the job.',
    bindSuccess: 'Added to Job Talent',
    viewJobTalent: 'View',
    bindFailed: 'Failed to add to job',
    viewDetail: 'View details',
    detailTitle: 'Candidate details',
    socialProfiles: 'Candidate social profiles',
    // C3: one icon per profile link on the candidate card; unverified ones carry profileLinkUnverified.
    profileLinks: {
      linkedin: 'LinkedIn',
      github: 'GitHub',
      website: 'Personal website',
      orcid: 'ORCID',
      google_scholar: 'Google Scholar',
      dblp: 'DBLP',
      semantic_scholar: 'Semantic Scholar',
      twitter: 'X (Twitter)',
      other: 'Other profile',
    },
    profileLinkAria: "Open {name}'s {platform} in a new tab",
    profileLinkUnverified: "A link from the candidate's profile, not verified as theirs",
    summary: 'Profile Summary',
    matchReason: 'Why Matched',
    evidence: 'Match Evidence',
    // Owner decision 2026-10-28: the search's evidence codes as readable chips (evidenceLabels.ts).
    evidenceLabels: {
      academicSource: 'Function and academic work match',
      titleSameSubFunction: 'Same sub-function',
      titleSameFunction: 'Same function',
      functionMatch: 'Job function match',
      seniorityMatch: 'Seniority match',
      seniorityHeldEarlier: 'Held this seniority earlier',
      industryCurrentCompany: 'Industry match (current company)',
      industryPastCompany: 'Industry match (past company)',
      industryParentCurrentCompany: 'Same industry group (current company)',
      industryParentPastCompany: 'Same industry group (past company)',
      skillGroupsFull: 'All required skills',
      skill: 'Skill: {name}',
    },
    publications: 'Publications',
    projects: 'Projects',
    projectStars: '{count} stars',
    openInNewTab: 'Open {name} in a new tab',
    latestBackground: 'Latest background',
    resumeUnavailable: {
      noRun: "This result is from an earlier search, so the full resume can't be loaded. Run a new search to see it.",
      notFound: "This recommendation result has expired, so the full resume can't be loaded. Run a new search.",
      failed: "Couldn't load the full resume. Please try again later.",
    },
    get: 'Get',
    unlockContact: 'Unlock Contact',
    unlockContactWithCost: 'Unlock for {count} contact points',
    unlockContactSuccess: 'Contact details unlocked',
    unlockContactFailed: 'Failed to unlock contact details',
    unlockContactInsufficient: 'Insufficient contact points. Purchase or upgrade to unlock.',
    noUnlockableContact: 'No contact details are available for this candidate right now',
    resultExpired: 'This recommendation result has expired. Run a new search.',
  },
  feedback: {
    approve: 'Approve',
    reject: 'Reject',
    whyApprove: 'Why approve?',
    whyReject: 'Why reject?',
    boundRejectHint: 'Rejecting this recommendation does not remove the candidate from the job.',
    notePlaceholder: 'Optional note',
    cancel: 'Cancel',
    confirmApprove: 'Confirm approve',
    confirmReject: 'Confirm reject',
    approved: 'Approved',
    rejected: 'Rejected',
    undo: 'Undo',
    reasonCompanyFit: 'Company background fits',
    reasonMultiUnit: 'Multi-unit / regional management',
    reasonRelevantExperience: 'Relevant franchise experience',
    reasonBilingual: 'Bilingual',
    reasonRightSeniority: 'Right seniority',
    reasonWrongSeniority: 'Wrong seniority',
    reasonWrongIndustry: 'Wrong industry',
    reasonNoRelevantExperience: 'No relevant experience',
    reasonTooJunior: 'Too junior',
    reasonTooFar: 'Location too far',
  },
  proposal: {
    progressIntro: 'Updating the search strategy.',
    progressLabel: 'Saving the confirmed changes',
    progressDetail: 'The latest strategy will appear once saved',
    confirmTitle: 'Update this search strategy?',
    acceptChanges: 'Apply the following suggested changes',
    confirm: 'Confirm',
    otherLabel: 'Additional input',
    otherPlaceholder: 'Add the conditions you want to keep or change...',
    retryOther: 'Retry suggestions',
    updated: 'Strategy updated',
    updateDetails: 'Strategy change details',
    retrySave: 'Retry save',
    apply: 'Apply these changes and search again',
    applying: 'Saving...',
    applied: 'Applied',
    appliedVersion: 'Applied · strategy version {version}',
    loading: 'Loading the changes...',
    unavailable: 'The change details could not be loaded. Saving is unavailable',
    noChanges: 'No actual changes. The strategy is unchanged',
    mismatch: 'The change details do not match. Saving is unavailable',
    stale: 'The strategy changed. This suggestion no longer applies',
    gone: 'This suggestion no longer exists',
    toast: {
      versionConflict: 'The strategy changed. The latest version is loaded; this suggestion no longer applies',
      searchNotStarted: 'The strategy is saved, but the LinkedIn search did not start: {reason}',
      operationConflict: 'The strategy was not saved. Try again',
      notFound: 'This suggestion no longer exists',
      rejected: 'These strategy changes cannot be saved',
      applyFailed: 'The strategy could not be saved because of a network or service error. Try again',
    },
    searchReasons: {
      versionConflict: 'the strategy changed again and the latest version is loaded',
      cancelled: 'cancelled',
      busy: 'another LinkedIn search is starting',
    },
  },
  agentQuestion: {
    label: 'Question',
    count: '{current} / {total}',
    previous: 'Previous question',
    confirmAnswer: 'Confirm answer',
    later: 'Answer later',
    options: 'Answer options',
    supplementLabel: 'Additional answer',
    supplementPlaceholder: 'Or add your own requirements…',
    confirm: 'Confirm',
    submitting: 'Submitting…',
    retry: 'Retry',
    failed: 'Not submitted. Your answer is kept; try again',
    deferred: 'You can answer later. The current search conditions stay unchanged.',
    deferredClarify: 'You can answer this later.',
    resume: 'Continue answering',
    summary: {
      field: '{label}: {change}',
      remove: 'remove {values}',
      add: 'add {values}',
      changeTo: 'change to {value}',
      moreCompanies: '{values} and others ({count} companies)',
      moreItems: '{values} and others ({count} items)',
      listSeparator: ', ',
      partSeparator: '; ',
      fieldSeparator: '. ',
    },
    modify: {
      question: '{changes}. Other conditions stay the same. Update the search strategy as suggested?',
      draftQuestion: '{changes}. Other conditions stay the same. Update the search strategy with this revised draft?',
      accept: 'Apply this change',
      revise: 'I want a different change',
    },
    batch: {
      question: 'Source in batches by {field}: first {first}, then {rest}. Can I start with this batch?',
      questionSingle: 'Source in batches by {field}: first {first}. Can I start with this batch?',
      summary: 'Source in batches by {field}: first {first}',
      accept: 'Yes, start with this batch',
      revise: 'Change the order or the split',
    },
    continue: {
      question: 'Keep the strategy and continue from where the last round stopped. Continue sourcing with the current strategy?',
      accept: 'Continue sourcing with the current strategy',
      revise: 'I want to adjust the strategy',
    },
  },
  linkedin: {
    title: 'LinkedIn Auto-Search',
    startButton: 'Search now',
    runningButton: 'Starting search...',
    retryButton: 'Search again',
    extensionConnected: 'Extension connected',
    extensionChecking: 'Checking extension...',
    extensionMissing: 'The EasyHire extension is not connected.',
    salesFeedbackPluginUpdateRequired: 'Update the EasyHire extension to use Sales Navigator feedback and experience ranges.',
    installExtension: 'Install extension',
    recheck: 'Check again',
    missingJob: 'Create a recruiting project before starting LinkedIn search.',
    confirmTitle: 'Start LinkedIn search',
    confirmMessage: 'Create tag "{tagName}" and start collecting candidates on LinkedIn?',
    confirmButton: 'Start search',
    started: 'Search started. The extension will collect candidates on LinkedIn.',
    failedDetail: 'Could not start LinkedIn search: {message}',
    connectionLost: 'The LinkedIn page closed or the extension connection was interrupted. The search may still be running; click search again to keep waiting or abandon it.',
    pluginNoAnswer: 'The extension did not confirm the search in time. It may still be running; click search again to keep waiting or abandon it.',
    uncertain: {
      message: 'The last search may still be running',
      continueWaiting: 'Keep waiting',
      abandonAndSearch: 'Abandon and search again',
    },
    abandonConfirm: {
      research: {
        title: 'Abandon and search again',
        message: 'The previous LinkedIn search will stop updating and get no Agent reply. If the extension is still working on the LinkedIn page, stop it in the extension. Then tag "{tagName}" is created and a search starts with the current strategy.',
        button: 'Abandon and search again',
      },
      stop: {
        title: 'Abandon this search',
        message: 'This LinkedIn search will stop updating and get no Agent reply. If the extension is still working on the LinkedIn page, stop it in the extension.',
        button: 'Abandon this search',
      },
    },
    feedback: {
      running: 'LinkedIn search in progress',
      analysing: 'Results submitted, the Agent is analysing',
      saving: 'Analysis ready, saving feedback',
      failed: 'Feedback could not be saved',
      authorization: 'Sign in to continue receiving feedback',
      recoverable: 'Reply pending',
      retry: 'Retry',
      accepted: '{count} accepted',
      stoppedByUser: 'Stopped manually, the data is partial',
      abandoned: 'Abandoned',
      stale: 'Nothing received for {minutes} min',
      abandon: 'Abandon this search',
      abandonFailed: 'This search could not be abandoned. Try again later',
    },
    rejected: {
      searchActive: 'A LinkedIn search for this job is already running',
      runEnded: 'This LinkedIn search has already ended. Click again to start a new one',
      missingJobTitle: 'The search strategy has no job title',
      versionUnavailable: 'There is no search strategy to search with yet',
      operationConflict: 'The LinkedIn search could not be started. Click search again',
      startUnavailable: 'The LinkedIn search could not be started. Try again later',
      startNetwork: 'The LinkedIn search could not be started because of a network error. Try again',
      legacyActive: 'An earlier LinkedIn task for this job needs attention',
      abandonFailed: 'The previous LinkedIn search could not be abandoned. Try again later',
      sourceAlreadyContinued: 'This round has already been continued with the current strategy',
      continueUnavailable: 'Sourcing cannot continue from where the last round stopped. Adjust the strategy and search again',
    },
  },
}

export default recruitingAiV2
